import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
} from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../../components/shared/Spinner";
import { getMergedTrackerOrders } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useTranslation, translateHeaders } from "../../../utils/useTranslation";
import { chinaShipmentTranslationMap } from "../../../utils/translations";
import { ChinaTrackerDataTableWithPagination3 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination3";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import moment from "moment";
import { DataTable } from "react-native-paper";

interface ChinaRecentShipmentsModalProps {
  visible: boolean;
  onClose: () => void;
}

// Grouped by container
const parentHeaders = {
  containerNo: {
    name: "Container No",
    width: 0.25,
  },
  location: {
    name: "Location",
    width: 0.18,
  },
  totalQty: {
    name: "Total Shipped Qty",
    width: 0.22,
    textAlign: "right",
  },
  itemCount: {
    name: "Total Items",
    width: 0.18,
    textAlign: "right",
  },
  updatedAt: {
    name: "Last Updated",
    width: 0.32,
  },
  status: {
    name: "Status",
    width: 0.25,
  },
};

// Expanded view per container
const nestedHeaders = {
  orderNo: {
    name: "Order No.",
    width: 0.15,
  },
  itemCode: {
    name: "Item Code",
    width: 0.2,
  },
  itemName: {
    name: "Item Name",
    width: 0.5,
  },
  shipmentQty: {
    name: "Qty",
    width: 0.2,
  },
  BPName: {
    name: "Customer Name",
    width: 0.35,
  },
  orderDate: {
    name: "Order Date",
    width: 0.25,
  },
  requestDate: {
    name: "Request Date",
    width: 0.25,
  },
};

const ChinaRecentShipmentsModal: React.FC<ChinaRecentShipmentsModalProps> = ({
  visible,
  onClose,
}) => {
  const listRef = useRef<ScrollView>(null);
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();

  const { isLargeDevice, isSmallDevice, isMediumDevice } = useContext(
    DeviceOrientationContext,
  );

  const allOrders = useSelector((state: any) => state.chinaTracker.allOrders);
  const shipments = useSelector(
    (state: any) => state.chinaTracker.shipments?.shipments || [],
  );
  const incomingShipments = useSelector(
    (state: any) => state.chinaTracker.shipments?.incoming?.shipments || [],
  );
  const isChinaTrackerLoaded = useSelector(
    (state: any) => state.chinaTracker.isChinaTrackerLoaded,
  );
  const loading = useSelector((state: any) => state.chinaTracker.loading);

  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [sortDirection, setSortDirection] = useState<"recent" | "oldest">("recent");
  const [dateFilter, setDateFilter] = useState<"today" | "week" | "month" | "last_week" | "last_month" | "all">("week");

  useEffect(() => {
    if (visible && !isChinaTrackerLoaded) {
      dispatch(getMergedTrackerOrders({ refresh: false }));
    }
  }, [visible, isChinaTrackerLoaded]);

  // Refresh data when modal opens
  useEffect(() => {
    if (visible) {
      dispatch(getMergedTrackerOrders({ refresh: true, showLoading: false }));
    }
  }, [visible]);

  // Group shipments by containerNo
  const groupedShipments = useMemo(() => {
    if (!allOrders || !Array.isArray(allOrders)) return [];

    const generatedContainerNos = new Set([
      ...shipments.map((s: any) => String(s.containerNo || "").trim().toLowerCase()),
      ...incomingShipments.map((s: any) => String(s.containerNo || "").trim().toLowerCase()),
    ]);

    const containerMap: Record<string, {
      containerNo: string;
      location: string;
      totalQty: number;
      totalItems: number;
      generatedBy: string;
      updatedAt: string;
      status: string;
      items: any[];
    }> = {};

    for (const order of allOrders) {
      if (order.details && Array.isArray(order.details)) {
        for (const detail of order.details) {
          if (detail.shipments && Array.isArray(detail.shipments)) {
            for (const shipment of detail.shipments) {
              const containerNo = shipment.containerNo || "";
              if (!containerNo) continue;

              if (!containerMap[containerNo]) {
                const isGenerated = generatedContainerNos.has(String(containerNo).trim().toLowerCase());
                containerMap[containerNo] = {
                  containerNo,
                  location: order.StateDelivery || shipment.location || "",
                  totalQty: 0,
                  totalItems: 0,
                  generatedBy: shipment.generatedBy || "",
                  updatedAt: shipment.updatedAt || shipment.createdAt || "",
                  status: isGenerated ? t("generatedStatus") : t("notGeneratedStatus"),
                  items: [],
                };
              }

              // Calculate qty for this shipment item
              const qty = shipment.shipmentQty || 0;
              containerMap[containerNo].totalQty += qty;
              containerMap[containerNo].totalItems += 1;

              // Track the most recent update date
              const currentDate = new Date(containerMap[containerNo].updatedAt).getTime();
              const shipmentDate = new Date(shipment.updatedAt || shipment.createdAt || "").getTime();
              if (shipmentDate > currentDate) {
                containerMap[containerNo].updatedAt = shipment.updatedAt || shipment.createdAt || "";
              }

              containerMap[containerNo].items.push({
                orderNo: order.OrderNumber1 || "",
                BPName: order.BPName || "",
                itemCode: order.ItemCode || "",
                itemName: order.ItemName || "",
                shipmentQty: qty,
                orderDate: order.OrderDate ? moment(order.OrderDate, ["DD-MMM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"]).format("DD/MM/YYYY") : "",
                requestDate: order.RequestDate ? moment(order.RequestDate, ["DD/MM/YYYY", "DD-MMM-YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"]).format("DD/MM/YYYY") : "",
              });
            }
          }
        }
      }
    }

    let result = Object.values(containerMap).map((container) => ({
      ...container,
      itemCount: container.items.length,
    }));

    // Sort by updatedAt
    result.sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime();
      const dateB = new Date(b.updatedAt).getTime();
      return sortDirection === "recent" ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [allOrders, sortDirection, shipments, incomingShipments, t]);

  // Filter by search text and date range
  const filteredShipments = useMemo(() => {
    let result = groupedShipments;

    // Date range filter
    if (dateFilter !== "all") {
      const now = moment().endOf("day").toDate();
      let startDate: Date;
      let endDate: Date = now;

      if (dateFilter === "today") {
        startDate = moment().startOf("day").toDate();
      } else if (dateFilter === "week") {
        startDate = moment().startOf("isoWeek").toDate(); // Monday start
      } else if (dateFilter === "month") {
        startDate = moment().startOf("month").toDate();
      } else if (dateFilter === "last_week") {
        startDate = moment().subtract(1, "week").startOf("isoWeek").toDate();
        endDate = moment().subtract(1, "week").endOf("isoWeek").toDate();
      } else if (dateFilter === "last_month") {
        startDate = moment().subtract(1, "month").startOf("month").toDate();
        endDate = moment().subtract(1, "month").endOf("month").toDate();
      } else {
        startDate = new Date(0);
      }

      result = result.filter((s) => {
        const shipDate = moment(s.updatedAt).valueOf();
        return shipDate >= startDate.getTime() && shipDate <= endDate.getTime();
      });
    }

    // Search text filter
    if (searchText && typeof searchText === "string" && searchText.trim()) {
      const query = searchText.toLowerCase();
      result = result.filter(
        (s) =>
          String(s.containerNo || "").toLowerCase().includes(query) ||
          String(s.location || "").toLowerCase().includes(query) ||
          String(s.generatedBy || "").toLowerCase().includes(query) ||
          String(s.status || "").toLowerCase().includes(query) ||
          (s.items && Array.isArray(s.items) && s.items.some(
            (item: any) =>
              String(item.orderNo || "").toLowerCase().includes(query) ||
              String(item.itemCode || "").toLowerCase().includes(query) ||
              String(item.BPName || "").toLowerCase().includes(query),
          )),
      );
    }

    return result;
  }, [groupedShipments, searchText, dateFilter]);

  const handleSearch = (text: string) => {
    setPage(0);
    setSearchText(text);
    if (text && text.trim()) {
      setDateFilter("all");
    }
  };

  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  const translatedParentHeaders = translateHeaders(
    parentHeaders,
    t,
    chinaShipmentTranslationMap,
  );

  const translatedNestedHeaders = translateHeaders(
    nestedHeaders,
    t,
    chinaShipmentTranslationMap,
  );

  const tableContent = (
    <>
      {loading ? (
        <View>
          <Spinner
            size="small"
            color="#74BBFF"
            styles={{ flexDirection: "row", margin: "20%" }}
          />
        </View>
      ) : (
        <View>
          <ChinaTrackerDataTableWithPagination3
            isToExpandAllRows={false}
            isRowsExpandable={true}
            headers={translatedParentHeaders}
            rows={filteredShipments}
            itemsPerPage={10}
            isDisplayTimeWithDate={true}
            useModal={true}
            isDetail={false}
            isEdit={false}
            isDelete={false}
            actionRows={[]}
            setActionRows={() => { }}
            itemTracker={true}
            setModalVisible={() => { }}
            handleRowClick={() => { }}
            setSelected={() => { }}
            handleDeleteRow={() => { }}
            t={t}
            page={page}
            setPage={setPage}
            handleSortColumn={() => { }}
            operationActions={() => { }}
            nestedHeader={translatedNestedHeaders}
            nestedItemKeyValue="items"
            rowKeyValue="containerNo"
            columnsToNotHaveThousandSeparator={["Order No.", "orderNo", "Total Items", "Order Date", "Request Date", "Status", "status"]}
            hidePagination={true}
          />
        </View>
      )}
    </>
  );

  const titleAndSearch = (
    <View
      style={[
        styles.titleContainer,
        isSmallDevice && { flexDirection: "column" },
      ]}
    >
      <Text style={[styles.titleText, isSmallDevice && styles.titleTextSmall]}>
        Recent Shipments ({filteredShipments.length})
      </Text>
      <View style={styles.searchContainer}>
        <LabelBannerWithSearchBar
          titleName={``}
          handleSearch={handleSearch}
          setRefresh={() => { }}
          dataToExport={filteredShipments}
          fileName="RecentShipments"
          sheetName="Recent Shipments"
        />
      </View>
    </View>
  );

  const filterBar = (
    <View style={styles.filterBarContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterBarContent}>
        {[
          { key: "all" as const, label: "All" },
          { key: "today" as const, label: "Today" },
          { key: "week" as const, label: "This Week" },
          { key: "month" as const, label: "This Month" },
          { key: "last_week" as const, label: "Last Week" },
          { key: "last_month" as const, label: "Last Month" },
        ].map((filter) => (
          <TouchableOpacity
            key={filter.key}
            onPress={() => {
              setDateFilter(filter.key);
              setPage(0);
            }}
          >
            <Text
              style={
                dateFilter === filter.key
                  ? styles.selectedFilter
                  : styles.filterLabel
              }
            >
              {filter.label}
            </Text>
          </TouchableOpacity>
        ))}

        <View style={styles.chipDivider} />

        <TouchableOpacity onPress={() => setSortDirection("recent")}>
          <Text
            style={
              sortDirection === "recent"
                ? styles.selectedFilter
                : styles.filterLabel
            }
          >
            ↓ Recent
          </Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setSortDirection("oldest")}>
          <Text
            style={
              sortDirection === "oldest"
                ? styles.selectedFilter
                : styles.filterLabel
            }
          >
            ↑ Oldest
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );

  const pagination = filteredShipments.length > 0 ? (
    <DataTable.Pagination
      page={page}
      numberOfPages={Math.ceil(filteredShipments.length / 10)}
      onPageChange={(page) => setPage(page)}
      label={`${Math.min((page + 1) * 10, filteredShipments.length)} of ${filteredShipments.length}`}
      showFastPaginationControls
      numberOfItemsPerPage={10}
      style={{ alignSelf: "flex-end", marginRight: 16 }}
    />
  ) : null;

  const footer = (
    <View style={[styles.footer, isSmallDevice && styles.footerSmall]}>
      <TouchableOpacity
        onPress={onClose}
        style={[styles.closeButton, isSmallDevice && styles.buttonSmall]}
      >
        <Text style={isSmallDevice && styles.buttonTextSmall}>
          {t("close")}
        </Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        {isLargeDevice ? (
          <View style={[styles.modalContainer, { width: getModalWidth() }]}>
            <View
              style={[
                styles.modalContainerSecond,
                { width: "100%", height: "100%" },
              ]}
            >
              {titleAndSearch}
              {filterBar}
              <ScrollView ref={listRef}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator
                  contentContainerStyle={{
                    minWidth: "100%",
                  }}
                >
                  <View
                    style={[
                      styles.containerContent,
                      { minWidth: "100%" },
                    ]}
                  >
                    {tableContent}
                  </View>
                </ScrollView>
              </ScrollView>
              {pagination}
              {footer}
            </View>
          </View>
        ) : (
          <View
            style={[
              styles.modalContainerSecond,
              { width: "100%", height: "90%" },
            ]}
          >
            {titleAndSearch}
            {filterBar}
            <ScrollView ref={listRef}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator
                contentContainerStyle={{
                  minWidth: "100%",
                }}
              >
                <View
                  style={[
                    styles.containerContent,
                    { minWidth: "100%" },
                  ]}
                >
                  {tableContent}
                </View>
              </ScrollView>
            </ScrollView>
            {pagination}
            {footer}
          </View>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.15)",
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 20,
    elevation: 12,
    borderRadius: 12,
    width: "100%",
    maxHeight: "95%",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 12,
    borderRadius: 12,
    padding: 16,
    width: "100%",
    height: "95%",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
    marginBottom: 12,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
  },
  titleTextSmall: {
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  sortChipContainer: {
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  filterBarContainer: {
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  filterBarContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  chipDivider: {
    width: 1,
    height: 24,
    backgroundColor: "#D1D5DB",
    marginHorizontal: 8,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: "500",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#F1F5F9",
    color: "#4B5563",
    overflow: "hidden",
  },
  selectedFilter: {
    fontSize: 12,
    fontWeight: "600",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#56B7FD",
    color: "#fff",
    overflow: "hidden",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 20,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    width: 120,
    alignItems: "center",
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
});

export default ChinaRecentShipmentsModal;
